import cc from 'classcat'
import Button from 'components/Button'
import UserCard from 'components/UserCard'
import Link from 'components/Link'
import { useSpring, config, animated } from 'react-spring'
import { isOfType } from 'utils/types.util'
import { useAuth } from 'clients/auth.client'
import useToggle from 'hooks/useToggle'
import { useFeatureTesting } from 'stores/featureTesting.store'
import Image from 'next/legacy/image'
import { useSettingsMenu, useProfileMenu } from 'components/AsidePageMenu'
import { FC } from 'react'
import withRouterReady from 'hocs/withRouterReady'
import { useRouter } from 'next/router'
import NextLink from 'next/link'
import SupportLink from 'components/SupportLink'
import useAdminMenu from 'hooks/useAdminMenu'
import IconBadge from 'assets/icons/badge.svg'
import { useTranslation } from 'next-i18next'
import IconArrow from 'assets/icons/arrow-payment.svg'
import BetaIcon from 'assets/icons/icons8-beta-button-50.svg'
import IconLinkBilling from 'assets/icons/icons8-billing-50.svg'
import IconLinkProfile from 'assets/icons/icons8-customer-50.svg'
import IconLinkDashboard from 'assets/icons/icons8-dashboard-50.svg'
import IconLinkInbox from 'assets/icons/icons8-inbox-50.svg'
import IconLinkMarketplace from 'assets/icons/icons8-online-store-50.svg'
import IconLinkSettings from 'assets/icons/icons8-smart-card-50.svg'

type Props = {
  isOpen: boolean
  toggle: () => void
}

type Type = 'primary' | 'secondary' | 'logout'

type MenuLink = {
  to?: string | MenuLink[]
  onClick?: () => void
  text: string
  $type?: Type
  initialOpenState?: boolean
  Icon?: any
}

type MenuCustomLink = {
  Component: FC
}

const Menu = ({ isOpen, toggle }: Props) => {
  const { t } = useTranslation()
  const settingsMenu = useSettingsMenu()
  const profileMenu = useProfileMenu()
  const overlayStyle = useSpring({
    from: { display: 'none', opacity: 0 },
    to: [
      {
        display: 'block',
        opacity: isOpen ? 1 : 0,
      },
      {
        display: isOpen ? 'block' : 'none',
      },
    ],
    config: config.stiff,
  })
  const sideStyle = useSpring({
    left: isOpen ? 0 : -300,
  })
  const { isAuthenticated, account } = useAuth()
  const searchFeatureTesting = useFeatureTesting('search')
  const proposalsFeatureTesting = useFeatureTesting('proposals')
  const subscriptionsFeatureTesting = useFeatureTesting(
    'packages_subscriptions',
  )
  const proFeatureTesting = useFeatureTesting('pro')
  const adminMenu = useAdminMenu()

  if (!isAuthenticated) {
    return null
  }

  return (
    <>
      {/* Overlay */}
      <animated.div
        className="z-7 fixed inset-0 bg-black bg-opacity-60 backdrop-filter backdrop-blur"
        style={overlayStyle}
        onClick={() => {
          toggle()
        }}
      />

      {/* Container */}
      <animated.div
        className="z-8 fixed top-0 left-0 flex flex-col w-72 h-full bg-white py-5 px-6 overflow-y-auto"
        style={sideStyle}
      >
        {/* TOP */}
        <div className="flex-grow">
          <button
            onClick={() => {
              toggle()
            }}
          >
            <Image
              src="/assets/icons/x.svg"
              alt="Close"
              width="20"
              height="20"
            />
          </button>

          {/* User */}
          <div className="py-2">
            <UserCard
              name={[account?.firstName, account?.lastName]
                .filter(Boolean)
                .join(' ')}
              username={account?.username || ''}
              picture={account?.picture || ''}
              onClick={() => {
                toggle()
              }}
            />
          </div>

          <div className="py-2">
            <Button
              href={
                account?.username
                  ? `/user/${account.username}`
                  : '/profile/edit'
              }
              $fluid
              onClick={() => {
                toggle()
              }}
            >
              {t('menu:buttonViewProfile')}
            </Button>
          </div>

          {proFeatureTesting.enabled && !account.subscription?.active && (
            <NextLink
              href="/pro"
              className="block bg-action-warning boujee-pro p-3 text-center font-bold text-white rounded-md uppercase flex items-center justify-center mt-2 mb-4"
              onClick={() => {
                toggle()
              }}
            >
              <IconBadge className="fill-white w-4 h-4 block" />
              <span className="pl-1 uppercase">{t('menu:buttonGetPro')}</span>
            </NextLink>
          )}

          {/* Menu (Top) */}
          <MenuLinks
            onClick={() => toggle()}
            links={
              [
                {
                  to: '/',
                  text: t<string>('menu:linkDashboard'),
                  Icon: IconLinkDashboard,
                },
                {
                  to: '/notifications#inbox',
                  text: t<string>('menu:linkNotifications'),
                  Icon: IconLinkInbox,
                },

                // Note: check for Fans and Brands
                {
                  text:
                    account.type === 'influencer'
                      ? t<string>('menu:linkSell')
                      : t<string>('menu:linkBuy'),
                  Icon: IconLinkBilling,
                  to: [
                    {
                      to: '/orders#current',
                      text: t<string>('menu:linkOrders'),
                    },
                    {
                      to: '/price-requests#received',
                      text: t<string>('menu:linkPriceRequests'),
                    },
                    {
                      to: '/reviews',
                      text: t<string>('menu:linkReviews'),
                    },
                  ],
                },

                searchFeatureTesting.enabled && {
                  to: '/search#influencers',
                  text: t<string>('menu:linkSearch'),
                },
                searchFeatureTesting.enabled && {
                  to: '/search#projects',
                  text: t<string>('menu:linkProjects'),
                },

                subscriptionsFeatureTesting.enabled && {
                  to: '/subscriptions',
                  text: t<string>('menu:linkSubscriptions'),
                },
                {
                  to: profileMenu(),
                  text: t<string>('menu:linkProfileSettings'),
                  Icon: IconLinkProfile,
                },

                {
                  to: settingsMenu(),
                  text: t<string>('menu:linkSettings'),
                  Icon: IconLinkSettings,
                },
                adminMenu.length > 0 && {
                  text: 'Admin',
                  Icon: IconLinkDashboard,
                  to: adminMenu,
                },

                {
                  to: '/marketplace',
                  text: (
                    <>
                      {t<string>('menu:linkMarketplace')}{' '}
                      <BetaIcon
                        className="inline-block fill-primary"
                        style={{
                          width: 40,
                          height: 25,
                        }}
                      />
                    </>
                  ),
                  Icon: IconLinkMarketplace,
                },
                {
                  to: 'https://influexer.com/premios-icono',
                  text: t<string>('menu:linkIconoMembership'),
                  Icon: IconLinkProfile,
                },
              ].filter(Boolean) as (MenuLink | MenuCustomLink)[]
            }
          />
        </div>

        {/* BOTTOM */}
        <div>
          <MenuLinks
            onClick={() => toggle()}
            links={
              [
                proposalsFeatureTesting.enabled && {
                  Component: () => (
                    <div className="py-5">
                      <Button $type="secondary" $fluid href="/create-proposal">
                        {t('menu:linkCreateProposal')}
                      </Button>
                    </div>
                  ),
                },
                {
                  Component: () => (
                    <SupportLink className="inline-block text-left w-full py-3 text-gray-600 hover:text-gray-800" />
                  ),
                },
                {
                  to: '/logout',
                  text: t<string>('menu:linkLogout'),
                  $type: 'logout',
                },
              ].filter(Boolean) as (MenuLink | MenuCustomLink)[]
            }
          />
        </div>
      </animated.div>
    </>
  )
}

type MenuLinksProps = {
  links: (MenuLink | MenuCustomLink)[]
  $type?: Type
  onClick?: () => void
}

const MenuLinks = ({ links, $type, onClick }: MenuLinksProps) => {
  const router = useRouter()

  return (
    <ul>
      {links.map((link, index) => (
        <li
          key={`menu-link-${index}`}
          className={cc([
            {
              'border-b border-gray-400':
                index < links.length - 1 || $type !== 'secondary',
            },
          ])}
        >
          {isOfType(link, 'Component') ? (
            <link.Component />
          ) : Array.isArray(link.to) ? (
            <SubMenu
              text={link.text}
              links={link.to.filter(Boolean)}
              onClick={onClick}
              initialOpenState={link.initialOpenState}
              Icon={link.Icon}
            />
          ) : link.to ? (
            <Link
              to={link.to}
              className="flex items-center w-full py-3"
              active={router.asPath === link.to}
              $type={link.$type || $type}
              data-testid={`link`}
              data-value={link.to}
              onClick={onClick}
              target={link.to.includes('http') ? '_blank' : '_self'}
            >
              {link.Icon && (
                <span className="block w-10">
                  <link.Icon className="fill-primary w-6 h-6" />
                </span>
              )}
              {link.text}
            </Link>
          ) : (
            <div />
          )}
        </li>
      ))}
    </ul>
  )
}

type SubMenuProps = {
  text: string
  links: (MenuLink | MenuCustomLink)[]
  onClick?: () => void
  initialOpenState?: boolean
  Icon?: any
}

const SubMenu = ({
  Icon,
  text,
  links,
  onClick,
  initialOpenState,
}: SubMenuProps) => {
  const [isOpen, toggle] = useToggle(initialOpenState)

  const openAnimation = useSpring({
    from: { opacity: '0', maxHeight: '0' },
    to: { opacity: '1', maxHeight: isOpen ? `${links.length * 50}px` : '0' },
  })

  return (
    <>
      <button
        className="w-full py-3"
        onClick={() => {
          toggle()
        }}
        data-testid="button-settings"
      >
        <span className="flex items-center">
          {Icon && (
            <span className="block w-10">
              <Icon className="fill-primary w-6 h-6" />
            </span>
          )}
          <span className="flex-grow text-left">{text}</span>
          <span>
            <IconArrow
              className="block w-12 w-12 w-2 transition-all opacity-60"
              style={{
                width: 24,
                height: 24,
                transform: isOpen ? `rotate(270deg)` : `rotate(90deg)`,
              }}
            />
          </span>
        </span>
      </button>

      <animated.div className="overflow-hidden" style={openAnimation}>
        <MenuLinks links={links} $type="secondary" onClick={onClick} />
      </animated.div>
    </>
  )
}

export default withRouterReady(Menu, { hideLoading: true })
